import { FMultiComboBox } from '@components/form/FMultiComboBox';
import { FNumericField } from '@components/form/FNumericField';
import { FSelectField } from '@components/form/FSelectField';
import { FSliderInput } from '@components/form/FSliderInput';
import { FTextAreaField } from '@components/form/FTextAreaField';
import { FTextField } from '@components/form/FTextField';
import { SkeletonInput } from '@components/skeleton';
import { useListMetersWithAssetSearch } from 'api/assets/assetsServices';
import { useFetchProjectCategories } from 'api/projects/projectsServices';
import { useFetchCompanyUsers } from 'api/user/userService';
import { addDays } from 'date-fns';
import { useAuth } from 'hooks/contexts/AuthContext';
import { useDebounce } from 'hooks/useDebounce';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import DatePickerRange from './DatePickerRange';
import { manageProjectControlNames } from './schema';
import { getUtilityIcon } from 'utils/utilityTypeUtils/utilityTypeUtils';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { highlightSearchTerm } from 'utils/textUtils/highlightSearchTerm';

interface ManageProjectFormProps {
  isEditing?: boolean;
  handleDeleteOpen?: () => void;
  isFetching?: boolean;
}

const ManageProjectForm: FC<ManageProjectFormProps> = ({
  isEditing = false,
  isFetching = false,
  handleDeleteOpen,
}) => {
  const [meterQuery, setMeterQuery] = useState('');
  const debouncedQuery = useDebounce(meterQuery, 300);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const { theme } = useTheme();

  const initialMeter = watch('meter');

  const referencePeriodEndDate = watch(
    manageProjectControlNames.referencePeriod.end
  );
  const implementationPeriodEndDate = watch(
    manageProjectControlNames.implementationPeriod.end
  );

  const implementationPeriodStartDate = useMemo(() => {
    if (referencePeriodEndDate) {
      return addDays(new Date(referencePeriodEndDate), 1).toISOString();
    }
  }, [referencePeriodEndDate]);

  const {
    data: meterList,
    isFetching: isFetchingMeters,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListMetersWithAssetSearch({
    query: debouncedQuery,
    perPage: 40,
    companyId: user?.company_id ?? null,
  });
  const { data: userList } = useFetchCompanyUsers({ perPage: 100 });
  const { data: categoriesList } = useFetchProjectCategories();

  const categoryOptions = useMemo(
    () =>
      categoriesList?.map?.((category) => ({
        value: category.key,
        name: category.value,
      })) ?? [],
    [categoriesList]
  );

  const userOptions = useMemo(
    () =>
      userList?.pages?.flatMap?.(
        (page) =>
          page?.users?.map?.((user) => ({
            name: `${user.given_name} ${user.family_name}`,
            value: user.id,
          }))
      ) ?? [],
    [userList]
  );

  const meterOptions = useMemo(() => {
    if (!meterList?.pages) return [];

    return meterList.pages.flatMap((page) =>
      page.meters.map((meter) => ({
        value: meter.id,
        name:
          meter.assets.length >= 1
            ? `${meter.assets[0].name} - ${meter.name}`
            : meter.name,
        customProperties: {
          buildingName: meter.assets[0].name,
          meterName: meter.name,
          utilityType: meter.utility_type,
        },
      }))
    );
  }, [meterList]);

  const metersInfiniteQueryKeys = useMemo(
    () => ['listMeters', debouncedQuery, String(user?.company_id ?? ''), ''],
    [debouncedQuery, user?.company_id]
  );

  useEffect(() => {
    setValue(manageProjectControlNames.responsible, user?.id ?? 1);
  }, [user]);

  useEffect(() => {
    if (implementationPeriodEndDate) {
      setValue(
        manageProjectControlNames.trackingPeriod.start,
        implementationPeriodEndDate
      );
    }
  }, [implementationPeriodEndDate, setValue]);

  return (
    <form className="flex flex-col gap-6">
      <div>
        {isEditing && (
          <div className="flex justify-end">
            {isFetching ? (
              <Skeleton width={80} />
            ) : (
              <span
                className="text-secondary-dark text-base cursor-pointer py-1 px-2"
                role={'button'}
                onClick={handleDeleteOpen}
              >
                Delete
              </span>
            )}
          </div>
        )}

        {isFetching ? (
          <SkeletonInput withLabel={true} labelWidth={100} />
        ) : (
          <FTextField
            label={t('common.inputs.name')}
            placeholder={t('projectsPage.placeholders.name')}
            name={manageProjectControlNames.name}
            autoComplete="off"
            id={'project-input'}
          />
        )}
      </div>

      {isFetching ? (
        <div>
          <Skeleton width={120} />
          <Skeleton height={130} />
        </div>
      ) : (
        <FTextAreaField
          label={t('common.inputs.description')}
          name={manageProjectControlNames.description}
          placeholder={t('projectsPage.placeholders.description')}
          draggable={false}
          rows={5}
          className="resize-none"
          autoComplete="off"
          id={'description-input'}
        />
      )}

      {isFetching ? (
        <SkeletonInput withLabel={true} labelWidth={100} />
      ) : (
        <FMultiComboBox
          label={t('common.meter')}
          initialValues={initialMeter}
          options={meterOptions}
          name={manageProjectControlNames.meter}
          placeholder={t('common.placeholders.selectMeter')}
          isFetching={isFetchingMeters}
          query={meterQuery}
          handleQuery={(value) => {
            setMeterQuery(value);
          }}
          maxHeight={300}
          maxSelected={1}
          disabled={isEditing}
          multiple={false}
          id={'meter-input'}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          infiniteQueryKeys={metersInfiniteQueryKeys}
          renderCustomOption={(options) => (
            <div className="flex items-start gap-1">
              <span className="mt-[2px]">
                {getUtilityIcon(
                  options?.customProperties?.utilityType ?? '',
                  theme.colors['gray-50'],
                  { height: 15, width: 15 }
                )}
              </span>

              <div className="flex flex-col">
                <span className="text-normal text-gray-60">
                  {highlightSearchTerm(
                    options?.customProperties?.meterName ?? '',
                    meterQuery
                  )}
                </span>
                <span className="italic text-sm text-gray-40">
                  {highlightSearchTerm(
                    options?.customProperties?.buildingName ?? '',
                    meterQuery
                  )}
                </span>
              </div>
            </div>
          )}
        />
      )}

      {isFetching ? (
        <div className="flex flex-col gap-10">
          <Skeleton width={160} />
          <Skeleton />
        </div>
      ) : (
        <FSliderInput
          label={t('projectsPage.savingsTarget')}
          name={manageProjectControlNames.savingsTarget}
          disabled={isEditing}
        />
      )}

      {isFetching ? (
        <SkeletonInput withLabel={true} labelWidth={100} />
      ) : (
        <FNumericField
          name={manageProjectControlNames.investmentCostNumber}
          placeholder={t('projectsPage.placeholders.investmentCost')}
          label={t('projectsPage.investmentCost')}
          onChangeValue={(e) => {
            setValue(manageProjectControlNames.investmentCostNumber, e);
          }}
          suffix={' €'}
          id={'investment-cost-input'}
          fixedDecimalScale={false}
        />
      )}

      {isFetching ? (
        <div className="flex gap-6">
          <SkeletonInput withLabel={true} labelWidth={100} />
          <SkeletonInput withLabel={true} labelWidth={100} />
        </div>
      ) : (
        <DatePickerRange
          label={t('projectsPage.referencePeriod')}
          tooltipMessage={t('projectsPage.referencePeriodTooltip')}
          startControlName={manageProjectControlNames.referencePeriod.start}
          endControlName={manageProjectControlNames.referencePeriod.end}
          yearRangeValidation={3}
          disabled={isEditing}
          id={'reference-range'}
          containerId="reference-container"
        />
      )}

      {isFetching ? (
        <div className="flex gap-6">
          <SkeletonInput withLabel={true} labelWidth={100} />
          <SkeletonInput withLabel={true} labelWidth={100} />
        </div>
      ) : (
        <DatePickerRange
          label={t('projectsPage.implementationPeriod')}
          tooltipMessage={t('projectsPage.implementationPeriodTooltip')}
          startControlName={
            manageProjectControlNames.implementationPeriod.start
          }
          endControlName={manageProjectControlNames.implementationPeriod.end}
          yearRangeValidation={3}
          initialDate={implementationPeriodStartDate}
          disabled={isEditing}
          id={'implementation-range'}
          containerId="implementation-container"
        />
      )}

      {isFetching ? (
        <div className="flex gap-6">
          <SkeletonInput withLabel={true} labelWidth={100} />
          <SkeletonInput withLabel={true} labelWidth={100} />
        </div>
      ) : (
        <DatePickerRange
          label={t('projectsPage.trackingPeriod')}
          tooltipMessage={t('projectsPage.trackingPeriodTooltip')}
          startControlName={manageProjectControlNames.trackingPeriod.start}
          endControlName={manageProjectControlNames.trackingPeriod.end}
          yearRangeValidation={3}
          disabledStart={true}
          disabledEnd={false}
          id={'tracking-range'}
          containerId="tracking-container"
        />
      )}

      {isFetching ? (
        <SkeletonInput withLabel={true} labelWidth={100} />
      ) : (
        <FSelectField
          label={t('common.category')}
          name={manageProjectControlNames.category}
          options={categoryOptions}
          placeholder={t('projectsPage.placeholders.category')}
        />
      )}

      {isFetching ? (
        <SkeletonInput withLabel={true} labelWidth={100} />
      ) : (
        <FSelectField
          label={t('common.responsible')}
          hidden={true}
          placeholder={t('common.placeholders.selectResponsible')}
          name={manageProjectControlNames.responsible}
          options={userOptions}
          value={user?.id ?? 1}
          disabled={true}
        />
      )}
    </form>
  );
};
export default ManageProjectForm;
